/**
 * Extract a user-displayable message from a thrown value.
 *
 * The shared `req()` helper at `common/api/request.js` rejects with
 * a plain JSON object (with `isApiError = true` and `.message`
 * populated from the server response) for HTTP failures — NOT an
 * Error instance. A naive `err instanceof Error ? err.message :
 * 'Unknown error'` therefore drops the useful Laravel 422/403
 * message for those rejections.
 *
 * This helper handles all three real-world reject shapes:
 *   - Error instances (network failures, JSON parse errors)
 *   - Laravel-style `{message, errors, isApiError}` plain objects
 *   - Anything else (string throw, undefined, etc.) → fallback
 */
export const extractApiErrorMessage = (
  err: unknown,
  fallback = 'Unknown error'
): string => {
  if (err instanceof Error && err.message) {
    return err.message
  }
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const candidate = (err as { message?: unknown }).message
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate
    }
  }
  if (typeof err === 'string' && err.length > 0) {
    return err
  }
  return fallback
}
